import * as React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

/**
 * Translated chrome for the design-system `DataTable` (its `labels` prop) and
 * `Pager`, from the common catalog. The shapes are structural so this package
 * does not depend on the design system; they match `DataTableLabels` and
 * `PagerLabels` there.
 */
export interface CommonTableLabels {
  readonly noRows: string;
  readonly previous: string;
  readonly next: string;
  readonly pageOf: (page: number, pageCount: number) => string;
  readonly navigation: string;
}

export interface CommonPagerLabels {
  readonly navigation: string;
  readonly previous: string;
  readonly next: string;
  readonly pageOf: (page: number, pageCount: number) => string;
  readonly page: (page: number) => string;
}

export function usePagerLabels(): CommonPagerLabels {
  const { t } = useTranslation('common');
  return React.useMemo(
    () => ({
      navigation: t('pager.navigation'),
      previous: t('pager.previous'),
      next: t('pager.next'),
      pageOf: (page: number, pageCount: number) => t('pager.pageOf', { page, pageCount }),
      page: (page: number) => t('pager.page', { page }),
    }),
    [t],
  );
}

export function useTableLabels(): CommonTableLabels {
  const { t } = useTranslation('common');
  return React.useMemo(
    () => ({
      noRows: t('table.noRows'),
      previous: t('pager.previous'),
      next: t('pager.next'),
      pageOf: (page: number, pageCount: number) => t('pager.pageOf', { page, pageCount }),
      navigation: t('pager.navigation'),
    }),
    [t],
  );
}

/** Labels for the design-system `ProvenanceFooter`. */
export function useProvenanceLabels(): {
  readonly build: string;
  readonly region: string;
  readonly environment: string;
  readonly serverTime: string;
  readonly signedBy: string;
  readonly cert: string;
} {
  const { t } = useTranslation('common');
  return React.useMemo(
    () => ({
      build: t('shell.provenance.build'),
      region: t('shell.provenance.region'),
      environment: t('shell.provenance.environment'),
      serverTime: t('shell.provenance.serverTime'),
      signedBy: t('shell.provenance.signedBy'),
      cert: t('shell.provenance.cert'),
    }),
    [t],
  );
}

/**
 * Fallback strings for the design-system components (`DesignSystemLabelsProvider`
 * there): close buttons, loading and error states, copy controls and the camera
 * tile badge. Each app provides them once at its root.
 */
export interface CommonComponentLabels {
  readonly pager: CommonPagerLabels;
  readonly table: CommonTableLabels;
  readonly close: string;
  readonly loading: string;
  readonly error: string;
  readonly retry: string;
  readonly copy: string;
  readonly copied: string;
  readonly copyFailed: string;
  readonly camera: {
    readonly live: string;
    readonly connecting: string;
    readonly offline: string;
    readonly cameraOffline: string;
  };
}

export function useComponentLabels(): CommonComponentLabels {
  const { t } = useTranslation('common');
  const pager = usePagerLabels();
  const table = useTableLabels();
  return React.useMemo(
    () => ({
      pager,
      table,
      close: t('actions.close'),
      loading: t('status.loading'),
      error: t('errors.generic'),
      retry: t('actions.retry'),
      copy: t('actions.copy'),
      copied: t('status.copied'),
      copyFailed: t('errors.copyFailed'),
      camera: {
        live: t('status.live'),
        connecting: t('video.tile.connecting'),
        offline: t('status.offline'),
        cameraOffline: t('video.tile.cameraOffline'),
      },
    }),
    [t, pager, table],
  );
}

/**
 * Labels for the design-system shell controls (`ThemeToggle`,
 * `LanguageToggle`, `AccountMenu`, `Breadcrumb`) and the sample-data notice.
 * Every shell passes these instead of keeping its own copies.
 */
export interface CommonShellLabels {
  readonly theme: {
    readonly toggle: string;
    readonly current: (theme: 'light' | 'dark' | 'hc') => string;
  };
  readonly language: {
    readonly toggle: string;
    readonly current: (locale: string) => string;
  };
  readonly account: { readonly menu: string; readonly signOut: string };
  readonly breadcrumb: string;
  readonly sampleData: { readonly label: string; readonly description: string };
}

/** Builds the shell labels from any `common`-namespace translate function. */
export function shellLabelsFrom(t: TFunction): CommonShellLabels {
  return {
    theme: {
      toggle: t('shell.theme.toggle'),
      current: (theme: 'light' | 'dark' | 'hc') =>
        t('shell.theme.current', { theme: t(`shell.theme.${theme}`) }),
    },
    language: {
      toggle: t('shell.language.toggle'),
      current: (locale: string) =>
        t('shell.language.current', {
          language: t(`shell.language.${locale}`, { defaultValue: locale }),
        }),
    },
    account: { menu: t('shell.account.menu'), signOut: t('actions.signOut') },
    breadcrumb: t('shell.breadcrumb'),
    sampleData: { label: t('sampleData.label'), description: t('sampleData.description') },
  };
}

export function useShellLabels(): CommonShellLabels {
  const { t } = useTranslation('common');
  return React.useMemo(() => shellLabelsFrom(t), [t]);
}
